import React from 'react';
import {
  WHATSAPP_ACCOUNT_KEY,
  WHATSAPP_EVENT_META,
  WHATSAPP_OPTIONS_KEY,
  isWhatsappAccountReady,
  mergeWhatsappAccountSettings,
  mergeWhatsappNotificationSettings,
} from '../../utils/whatsappNotificationSettings.js';

const WhatsAppNotificationSettingsFields = ({
  formData,
  onToggle,
  onFieldChange,
  onAccountChange,
  embedded = false,
}) => {
  if (!formData?.can_access?.whatsapp_notification) {
    return null;
  }

  const settings = mergeWhatsappNotificationSettings(
    formData.can_access?.[WHATSAPP_OPTIONS_KEY]
  );
  const account = mergeWhatsappAccountSettings(
    formData.can_access?.[WHATSAPP_ACCOUNT_KEY]
  );

  const accountReady = isWhatsappAccountReady(account);

  const handleAccountField = (name, value) => {
    onAccountChange?.({
      ...account,
      [name]: value,
    });
  };

  return (
    <div>
      {!embedded && (
        <>
          <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
            WhatsApp Notification Options
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 mb-3">
            Stored under company can_access. Messages send from this company Meta account.
            Create the same template names on that WABA as in the server .env.
          </p>
        </>
      )}
      {embedded && (
        <p className="text-xs sm:text-sm text-gray-500 mb-3">
          Enter this company WhatsApp Cloud API credentials. Template names stay the same
          as the platform templates — create them on this company Meta account.
          Choose sender / receiver / invoice per event.
        </p>
      )}
      <div className="border border-orange-200 bg-orange-50/40 rounded-xl p-3 sm:p-4 mb-4">
        <p className="text-sm font-semibold text-gray-800 mb-1">Company WhatsApp account</p>
        <p className="text-xs text-gray-500 mb-3">
          Phone Number ID and Access Token are required. Event options stay disabled until both are filled.
          Display name on WhatsApp is set in Meta.
        </p>
        {!accountReady && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            Fill Phone Number ID and Access Token to enable WhatsApp notification options.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Phone Number ID
            </label>
            <input
              type="text"
              value={account.phone_number_id || ''}
              onChange={(e) => handleAccountField('phone_number_id', e.target.value)}
              placeholder="Meta Phone Number ID"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-400"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Access Token
            </label>
            <input
              type="password"
              value={account.access_token || ''}
              onChange={(e) => handleAccountField('access_token', e.target.value)}
              placeholder={
                account.has_access_token
                  ? '•••••••• (leave blank to keep)'
                  : 'Permanent Meta access token'
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-400"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              API Version
            </label>
            <input
              type="text"
              value={account.api_version || ''}
              onChange={(e) => handleAccountField('api_version', e.target.value)}
              placeholder="v21.0 (optional)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-400"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              WABA ID
            </label>
            <input
              type="text"
              value={account.waba_id || ''}
              onChange={(e) => handleAccountField('waba_id', e.target.value)}
              placeholder="Optional WhatsApp Business Account ID"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-400"
              autoComplete="off"
            />
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {WHATSAPP_EVENT_META.map((event) => {
          const checkboxOptions = event.options.filter((option) => option.type !== 'text');
          const textOptions = event.options.filter((option) => option.type === 'text');
          return (
            <div
              key={event.key}
              className={`border rounded-xl p-3 sm:p-4 ${
                accountReady ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
            >
              <div className="mb-2">
                <p className="text-sm font-semibold text-gray-800">{event.label}</p>
                <p className="text-xs text-gray-500">{event.hint}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {checkboxOptions.map((option) => {
                  const checked = Boolean(settings?.[event.key]?.[option.key]);
                  const id = `wa-${event.key}-${option.key}`;
                  return (
                    <label
                      key={option.key}
                      htmlFor={id}
                      className={`flex items-center gap-2 p-2 border border-gray-100 rounded-lg ${
                        accountReady ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-not-allowed'
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={id}
                        checked={checked}
                        disabled={!accountReady}
                        onChange={() => accountReady && onToggle(event.key, option.key)}
                        className="w-4 h-4 text-orange-600 focus:ring-blue-400 border-gray-300 rounded disabled:opacity-50"
                      />
                      <span className="text-xs sm:text-sm text-gray-800">{option.label}</span>
                    </label>
                  );
                })}
              </div>
              {textOptions.map((option) => {
                const id = `wa-${event.key}-${option.key}`;
                const value = settings?.[event.key]?.[option.key] || '';
                return (
                  <div key={option.key} className="mt-3">
                    <label htmlFor={id} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {option.label}
                    </label>
                    <input
                      type="text"
                      id={id}
                      value={value}
                      disabled={!accountReady}
                      placeholder={option.placeholder || ''}
                      onChange={(e) => onFieldChange?.(event.key, option.key, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {option.placeholder && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        Leave blank to use the default template ({option.placeholder}).
                        Template must be approved on this company WhatsApp account.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WhatsAppNotificationSettingsFields;
